import React, { useRef, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './RemedierCards.css';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Whitelist af e-mails der må redigere
const ADMIN_EMAILS = [
  'christian.c.nielsen@gmail.com',
  'Mollelysgaard@hotmail.com',
  'kasper.anthon.soerensen@gmail.com',
  'Martindyrmanhansen@gmail.com',
  'mlundk@gmail.com',
  'kildetoft@fastmail.com',
];

function emptyCard() {
  return { id: 'temp-' + Date.now() + '-' + Math.random(), image_url: '', name: '', text: '', kategori: 'lorte-spell', count: 1, editing: true, isNew: true, uploading: false };
}

const cardStyle = {
  width: 300,
  height: 400,
  aspectRatio: '3/4',
  border: '2px solid #333',
  borderRadius: 16,
  background: '#fff',
  boxShadow: '0 2px 12px #0002',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
  margin: '24px auto',
  padding: 0,
  overflow: 'visible',
  position: 'relative',
  cursor: 'pointer',
};

const ImagePlusIcon = ({ onClick }) => (
  <div
    onClick={onClick}
    style={{
      width: 90,
      height: 90,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f3f3f3',
      border: '2px dashed #bbb',
      borderRadius: 12,
      cursor: 'pointer',
      margin: '0 auto 8px auto',
      position: 'relative',
    }}
    title="Tilføj billede"
  >
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <rect x="8" y="12" width="32" height="24" rx="4" fill="#e0e0e0" stroke="#bbb" strokeWidth="2" />
      <circle cx="18" cy="22" r="3" fill="#bbb" />
      <path d="M12 32l8-8 6 6 8-10 6 8" stroke="#bbb" strokeWidth="2" fill="none" />
      <rect x="20" y="20" width="8" height="8" fill="none" />
    </svg>
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ position: 'absolute', top: 8, right: 8 }}>
      <circle cx="9" cy="9" r="8" fill="#3a6ea5" />
      <rect x="8" y="4" width="2" height="8" rx="1" fill="#fff" />
      <rect x="4" y="8" width="8" height="2" rx="1" fill="#fff" />
    </svg>
  </div>
);

const RemedierCards = () => {
  const [cards, setCards] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // card id
  const [editBackup, setEditBackup] = useState({}); // { [id]: cardData }
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState({
    'lorte-spell': true,
    'super-lorte-spell': true,
    'master-lorte-spell': true,
  });
  const fileInput = useRef();
  // State til at styre eksport-container
  const [exporting, setExporting] = useState(false);
  const exportRefs = useRef([]);
  const [user, setUser] = useState(null);
  const [editAlert, setEditAlert] = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data?.user || null));
    const { data: listener } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user || null);
    });
    return () => listener?.subscription.unsubscribe();
  }, []);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Fetch cards from Supabase on mount
  useEffect(() => {
    fetchCards();
    // eslint-disable-next-line
  }, []);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase.channel('cards-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cards' }, payload => {
        if (payload.eventType === 'INSERT') {
          setCards(prev => [{ ...payload.new, editing: false, isNew: false, uploading: false }, ...prev.filter(c => c.id !== payload.new.id)]);
        } else if (payload.eventType === 'UPDATE') {
          setCards(prev => prev.map(c => c.id === payload.new.id ? { ...payload.new, editing: false, isNew: false, uploading: false } : c));
        } else if (payload.eventType === 'DELETE') {
          setCards(prev => prev.filter(c => c.id !== payload.old.id));
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  async function fetchCards() {
    setLoading(true);
    const { data, error } = await supabase
      .from('cards')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setCards(data.map(card => ({ ...card, count: card.count ?? 1, editing: false, isNew: false, uploading: false })));
    }
    setLoading(false);
  }

  async function handleAddCard() {
    if (window.confirm('Er du sikker på, at du vil tilføje et nyt kort?')) {
      setCards([{ ...emptyCard() }, ...cards]);
    }
  }

  function handleEditCard(id) {
    setCards(cards => cards.map(card => card.id === id ? { ...card, editing: true, isNew: String(card.id).startsWith('temp-') } : card));
    const card = cards.find(c => c.id === id);
    if (card) setEditBackup(b => ({ ...b, [id]: { ...card } }));
  }

  function handleDeleteCard(id) {
    setDeleteConfirm(id);
  }

  async function confirmDeleteCard(id) {
    setDeleteConfirm(null);
    setEditBackup(b => { const nb = { ...b }; delete nb[id]; return nb; });
    setCards(cards => cards.filter(card => card.id !== id));
    if (id && !String(id).startsWith('temp-')) await supabase.from('cards').delete().eq('id', id);
  }

  function cancelDelete() {
    setDeleteConfirm(null);
  }

  function handleChange(e, card) {
    const { name, value, files } = e.target;
    setCards(cards.map(c => {
      if (c.id !== card.id) return c;
      if (name === 'image' && files && files[0]) {
        // Set uploading state only for image
        return { ...c, uploading: true };
      }
      if (name === 'text') {
        return { ...c, text: value.slice(0, 100), uploading: false };
      }
      if (name === 'name') {
        return { ...c, name: value, uploading: false };
      }
      if (name === 'kategori') {
        return { ...c, kategori: value, uploading: false };
      }
      if (name === 'count') {
        let newCount = Math.max(1, Math.min(10, Number(value)));
        return { ...c, count: newCount };
      }
      return { ...c, [name]: value, uploading: false };
    }));
    if (name === 'image' && files && files[0]) {
      uploadImage(card, files[0]);
    }
  }

  async function uploadImage(card, file) {
    const filename = `${Date.now()}_${sanitizeFilename(file.name)}`;
    console.log('Uploading to bucket:', 'card-images');
    console.log('Filename:', filename);
    console.log('File:', file);

    const { data, error } = await supabase.storage.from('card-images').upload(filename, file, { upsert: true });
    console.log('Upload response:', data, error);

    if (!error && data) {
      const { publicUrl } = supabase.storage.from('card-images').getPublicUrl(filename).data;
      setCards(cards => cards.map(c => c.id === card.id ? { ...c, image_url: publicUrl, uploading: false } : c));
    } else {
      setCards(cards => cards.map(c => c === card ? { ...c, uploading: false } : c));
      alert('Billed-upload fejlede: ' + (error?.message || 'Ukendt fejl'));
    }
  }

  async function handleSave(card) {
    const savedCard = {
      name: card.name,
      text: card.text,
      image_url: card.image_url,
      kategori: card.kategori,
      count: Number(card.count) || 1
    };
    if (card.id && !String(card.id).startsWith('temp-')) {
      // Update
      const { data, error } = await supabase.from('cards').update(savedCard).eq('id', card.id).select();
      if (!error && data && data[0]) {
        setCards(cards => cards.map(c => c.id === card.id ? { ...data[0], editing: false, isNew: false, uploading: false } : c));
      }
    } else {
      // Insert
      const { data, error } = await supabase.from('cards').insert([savedCard]).select();
      if (!error && data && data[0]) {
        setCards(cards => cards.map(c => c.id === card.id ? { ...data[0], editing: false, isNew: false, uploading: false } : c));
      } else {
        // fallback: remove temp card if insert fails
        setCards(cards => cards.filter(c => c.id !== card.id));
      }
    }
    setEditBackup(b => { const nb = { ...b }; delete nb[card.id]; return nb; });
  }

  function handleCancel(card) {
    if (card.isNew || String(card.id).startsWith('temp-')) {
      setCards(cards => cards.filter(c => c.id !== card.id));
    } else {
      setCards(cards => cards.map(c => c.id === card.id ? { ...editBackup[card.id], editing: false, isNew: false, uploading: false } : c));
      setEditBackup(b => { const nb = { ...b }; delete nb[card.id]; return nb; });
    }
  }

  function triggerFileInput(cardId, e) {
    e.stopPropagation();
    if (fileInput.current[cardId]) {
      fileInput.current[cardId].click();
    }
  }

  if (!fileInput.current) fileInput.current = {};

  // Filtrér kort efter kategori (kun i afsluttende visning)
  const kategoriOrder = {
    'lorte-spell': 0,
    'super-lorte-spell': 1,
    'master-lorte-spell': 2,
  };
  // For at bevare placering: filtrér listen, men sorter kun afsluttede kort internt
  const filteredCards = (() => {
    // Bevar rækkefølgen, men sorter kun afsluttede kort internt
    // Alle kort i redigering inkluderes ALTID, uanset filter/kategori
    const editingIds = cards.filter(card => card.editing).map(card => card.id);
    // Find alle synlige kort (enten i redigering eller valgt i filter)
    const visible = cards.filter(card => card.editing || categoryFilter[card.kategori]);
    // Find alle indekser for kort der ikke er i redigering
    const doneIndexes = visible
      .map((card, idx) => (!card.editing ? idx : null))
      .filter(idx => idx !== null);
    // Lav en sorteret kopi af de afsluttede kort
    const sortedDone = doneIndexes
      .map(idx => visible[idx])
      .sort((a, b) => (kategoriOrder[a.kategori] ?? 99) - (kategoriOrder[b.kategori] ?? 99));
    // Erstat de afsluttede kort i listen med de sorterede, men bevar redigeringskort på deres plads
    let donePtr = 0;
    return visible.map(card => {
      if (card.editing) return card;
      return sortedDone[donePtr++];
    })
    // Tilføj evt. redigeringskort, der ikke er i visible (hvis kategori blev ændret til en ikke-valgt)
    .concat(cards.filter(card => card.editing && !visible.includes(card)));
  })();

  // Modal state: hvilket kort redigeres?
  const editingCard = cards.find(card => card.editing);

  // Tællere for hver kategori
  const countLorte = cards.filter(card => card.kategori === 'lorte-spell').reduce((sum, card) => sum + (card.count ?? 1), 0);
  const countSuper = cards.filter(card => card.kategori === 'super-lorte-spell').reduce((sum, card) => sum + (card.count ?? 1), 0);
  const countMaster = cards.filter(card => card.kategori === 'master-lorte-spell').reduce((sum, card) => sum + (card.count ?? 1), 0);

  // Størrelse til eksport (64mm x 89mm at 150 DPI)
  const EXPORT_WIDTH = 378;  // 64mm at 150 DPI
  const EXPORT_HEIGHT = 525; // 89mm at 150 DPI

  // Eksportér alle kort via skjult React-container
  async function handleExportAllCards() {
    setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500)); // Giv mere tid til at render eksport-container
    
    const exportableCards = cards.filter(card => !card.editing && card.name && card.text);
    const zip = new JSZip();
    
    for (let i = 0; i < exportRefs.current.length; i++) {
      const node = exportRefs.current[i];
      if (!node) continue;
      
      try {
        const canvas = await html2canvas(node, { 
          scale: 3, 
          useCORS: true, 
          backgroundColor: null,
          allowTaint: true,
          foreignObjectRendering: false,
          logging: false
        });
        const dataUrl = canvas.toDataURL('image/png', 1.0);
        zip.file(`kort_${i + 1}.png`, dataUrl.split(',')[1], { base64: true });
      } catch (error) {
        console.log(`Fejl ved eksport af kort ${i + 1}:`, error.message);
        // Fortsæt med næste kort i stedet for at stoppe
      }
    }
    
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'remedier_kort.zip');
    setExporting(false);
  }

  // Rens filnavne for Supabase Storage
  function sanitizeFilename(filename) {
    let clean = filename.replace(/\s+/g, '_');
    clean = clean.replace(/[^a-zA-Z0-9._-]/g, '');
    return clean;
  }

  // Gem tælleren automatisk
  async function updateCardCount(cardId, newCount) {
    try {
      const { error } = await supabase
        .from('cards')
        .update({ count: newCount })
        .eq('id', cardId);
      
      if (error) {
        console.error('Fejl ved opdatering af tæller:', error);
      }
    } catch (error) {
      console.error('Fejl ved opdatering af tæller:', error);
    }
  }

  // Håndter tæller op/ned
  async function handleCountChange(cardId, increment) {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;
    
    const currentCount = card.count ?? 1;
    const newCount = increment ? Math.min(10, currentCount + 1) : Math.max(1, currentCount - 1);
    
    // Opdater lokalt state
    setCards(cards => cards.map(c => c.id === cardId ? { ...c, count: newCount } : c));
    
    // Gem til Supabase
    await updateCardCount(cardId, newCount);
  }

  return (
    <div className="remedier-container">
      <div style={{ maxWidth: 400, margin: '0 auto', padding: 16 }}>
        <h1 style={{ textAlign: 'center', marginBottom: 24, color: '#000' }}>Remedier - Spillekort</h1>
        {/* Eksport-knap kun i desktop-visning */}
        {window.innerWidth > 700 && isAdmin && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <button onClick={handleExportAllCards} style={{ fontSize: '1rem', padding: '0.6em 2em', borderRadius: 8, background: '#22336c', color: '#fff', border: 'none', boxShadow: '0 2px 8px #0001', cursor: 'pointer' }}>
              Eksportér alle kort som billeder (zip)
            </button>
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {isAdmin && (
            <button onClick={handleAddCard} style={{ fontSize: '1.1rem', padding: '0.7em 2em', borderRadius: 8, background: '#3a6ea5', color: '#fff', border: 'none', boxShadow: '0 2px 8px #0001', cursor: 'pointer' }}>Tilføj kort</button>
          )}
        </div>
        {loading && <div style={{ textAlign: 'center', color: '#888', margin: 24 }}>Indlæser kort...</div>}
      </div>
      <div>
        {/* Kategori filter checkbokse */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={categoryFilter['lorte-spell']} onChange={e => setCategoryFilter(f => ({ ...f, 'lorte-spell': e.target.checked }))} />
            lorte <span style={{ background: '#eee', borderRadius: 8, padding: '2px 8px', fontSize: 13, marginLeft: 4 }}>{countLorte}</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={categoryFilter['super-lorte-spell']} onChange={e => setCategoryFilter(f => ({ ...f, 'super-lorte-spell': e.target.checked }))} />
            super <span style={{ background: '#ffe066', borderRadius: 8, padding: '2px 8px', fontSize: 13, marginLeft: 4 }}>{countSuper}</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={categoryFilter['master-lorte-spell']} onChange={e => setCategoryFilter(f => ({ ...f, 'master-lorte-spell': e.target.checked }))} />
            master <span style={{ background: '#22336c', color: '#fff', borderRadius: 8, padding: '2px 8px', fontSize: 13, marginLeft: 4 }}>{countMaster}</span>
          </label>
        </div>
        {/* Modal redigering */}
        {editingCard && isAdmin && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.25)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div>
              {/* Genbrug redigeringsvisningen for kortet */}
              {(() => {
                let card = editingCard;
                let bg = '#fff';
                let icon = null;
                let textBg = 'rgba(255,255,255,0.82)';
                let textColor = '#333';
                if (card.kategori === 'super-lorte-spell') {
                  bg = '#ffe066';
                  icon = '/lort2.png';
                  textBg = 'rgba(255,224,102,0.55)';
                } else if (card.kategori === 'master-lorte-spell') {
                  bg = '#22336c';
                  icon = '/lort3.png';
                  textBg = 'rgba(34,51,108,0.85)';
                  textColor = '#fff';
                } else {
                  bg = '#fff';
                  icon = '/lort1.png';
                  textBg = 'rgba(255,255,255,0.82)';
                  textColor = '#333';
                }
                return (
                  <div style={{ ...cardStyle, background: bg, position: 'relative', margin: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', padding: 24, overflowY: 'auto' }}>
                      {/* Billede og upload */}
                      <div
                        onClick={e => triggerFileInput(card.id, e)}
                        style={{
                          width: '100%',
                          height: 90,
                          marginBottom: 8,
                          borderRadius: 12,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          background: card.image_url ? `url(${card.image_url}) center/cover no-repeat` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2,
                        }}
                        title={card.image_url ? 'Skift billede' : 'Tilføj billede'}
                      >
                        {!card.image_url && <ImagePlusIcon />}
                        {card.uploading && <span style={{ position: 'absolute', top: 8, left: 8, color: '#3a6ea5', fontWeight: 600 }}>Uploader...</span>}
                        <input
                          type="file"
                          accept="image/*"
                          name="image"
                          ref={el => (fileInput.current[card.id] = el)}
                          onChange={e => handleChange(e, card)}
                          style={{ display: 'none' }}
                        />
                      </div>
                      {/* Kategori dropdown */}
                      <select
                        name="kategori"
                        value={card.kategori || 'lorte-spell'}
                        onChange={e => handleChange(e, card)}
                        style={{ marginBottom: 12, padding: 6, borderRadius: 6, border: '1px solid #bbb', fontSize: '1rem', background: 'rgba(255,255,255,0.85)' }}
                      >
                        <option value="lorte-spell">lorte-spell</option>
                        <option value="super-lorte-spell">super-lorte-spell</option>
                        <option value="master-lorte-spell">master-lorte-spell</option>
                      </select>
                      {/* Navn og tekst */}
                      <input
                        type="text"
                        name="name"
                        value={card.name}
                        onChange={e => handleChange(e, card)}
                        placeholder="Kortets navn (overskrift)"
                        style={{ width: '100%', marginBottom: 12, fontWeight: 'bold', fontSize: '1.25rem', borderRadius: 6, border: '1px solid #bbb', padding: 6, background: 'rgba(255,255,255,0.85)' }}
                      />
                      <textarea
                        name="text"
                        value={card.text}
                        onChange={e => handleChange(e, card)}
                        placeholder="Tekst på kortet (max 100 tegn)"
                        maxLength={100}
                        rows={3}
                        style={{ width: '100%', marginBottom: 12, fontSize: '1.1rem', borderRadius: 6, border: '1px solid #bbb', padding: 6, resize: 'none', background: 'rgba(255,255,255,0.85)' }}
                      />
                      <div style={{ fontSize: '1rem', color: '#888', marginBottom: 12 }}>{card.text.length}/100 tegn</div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 'auto', flexWrap: 'wrap' }}>
                        <button onClick={() => handleSave(card)} style={{ fontSize: '0.9rem', padding: '0.4em 1em', background: '#3a6ea5', color: '#fff', border: 'none', borderRadius: 6 }} disabled={card.uploading}>Gem</button>
                        <button onClick={() => handleCancel(card)} style={{ fontSize: '0.9rem', padding: '0.4em 1em', background: '#bbb', color: '#222', border: 'none', borderRadius: 6 }}>Annuller</button>
                        <button onClick={() => handleDeleteCard(card.id)} style={{ color: '#e63946', fontSize: '0.9rem', padding: '0.4em 1em', background: '#fff', border: '1px solid #e63946', borderRadius: 6 }}>Slet</button>
                      </div>
                      {deleteConfirm === card.id && (
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.95)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 16 }}>
                          <div style={{ marginBottom: 16, fontWeight: 600 }}>Er du sikker på du vil slette dette kort?</div>
                          <button onClick={() => confirmDeleteCard(card.id)} style={{ background: '#e63946', color: '#fff', border: 'none', borderRadius: 8, padding: '0.5em 1.5em', marginRight: 8 }}>Ja, slet</button>
                          <button onClick={cancelDelete} style={{ background: '#bbb', color: '#222', border: 'none', borderRadius: 8, padding: '0.5em 1.5em', marginTop: 8 }}>Annuller</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {/* Vis altid listen over kort */}
        {filteredCards.map(card => {
          // Kategori farve og ikon
          let bg = '#fff';
          let icon = null;
          let textBg = 'rgba(255,255,255,0.82)';
          let textColor = '#333';
          if (card.kategori === 'super-lorte-spell') {
            bg = '#ffe066';
            icon = '/lort2.png';
            textBg = 'rgba(255,224,102,0.55)';
          } else if (card.kategori === 'master-lorte-spell') {
            bg = '#22336c';
            icon = '/lort3.png';
            textBg = 'rgba(34,51,108,0.85)';
            textColor = '#fff';
          } else {
            bg = '#fff';
            icon = '/lort1.png';
            textBg = 'rgba(255,255,255,0.82)';
            textColor = '#333';
          }
          return (
            <div key={card.id || card.name + card.text} className="remedier-card-exportable" style={{ ...cardStyle, marginBottom: 28, background: bg, position: 'relative' }}>
              {/* Kun admins kan redigere */}
              {isAdmin && card.editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', padding: 24, overflowY: 'auto' }}>
                  {/* Billede og upload */}
                  <div
                    onClick={e => triggerFileInput(card.id, e)}
                    style={{
                      width: '100%',
                      height: 90,
                      marginBottom: 8,
                      borderRadius: 12,
                      overflow: 'hidden', // kun på billedet
                      cursor: 'pointer',
                      position: 'relative',
                      background: card.image_url ? `url(${card.image_url}) center/cover no-repeat` : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                    title={card.image_url ? 'Skift billede' : 'Tilføj billede'}
                  >
                    {!card.image_url && <ImagePlusIcon />}
                    {card.uploading && <span style={{ position: 'absolute', top: 8, left: 8, color: '#3a6ea5', fontWeight: 600 }}>Uploader...</span>}
                    <input
                      type="file"
                      accept="image/*"
                      name="image"
                      ref={el => (fileInput.current[card.id] = el)}
                      onChange={e => handleChange(e, card)}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {/* Kategori dropdown */}
                  <select
                    name="kategori"
                    value={card.kategori || 'lorte-spell'}
                    onChange={e => handleChange(e, card)}
                    style={{ marginBottom: 12, padding: 6, borderRadius: 6, border: '1px solid #bbb', fontSize: '1rem', background: 'rgba(255,255,255,0.85)' }}
                  >
                    <option value="lorte-spell">lorte-spell</option>
                    <option value="super-lorte-spell">super-lorte-spell</option>
                    <option value="master-lorte-spell">master-lorte-spell</option>
                  </select>
                  {/* Navn og tekst */}
                  <input
                    type="text"
                    name="name"
                    value={card.name}
                    onChange={e => handleChange(e, card)}
                    placeholder="Kortets navn (overskrift)"
                    style={{ width: '100%', marginBottom: 12, fontWeight: 'bold', fontSize: '1.25rem', borderRadius: 6, border: '1px solid #bbb', padding: 6, background: 'rgba(255,255,255,0.85)' }}
                  />
                  <textarea
                    name="text"
                    value={card.text}
                    onChange={e => handleChange(e, card)}
                    placeholder="Tekst på kortet (max 100 tegn)"
                    maxLength={100}
                    rows={3}
                    style={{ width: '100%', marginBottom: 12, fontSize: '1.1rem', borderRadius: 6, border: '1px solid #bbb', padding: 6, resize: 'none', background: 'rgba(255,255,255,0.85)' }}
                  />
                  <div style={{ fontSize: '1rem', color: '#888', marginBottom: 12 }}>{card.text.length}/100 tegn</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 'auto', flexWrap: 'wrap' }}>
                    <button onClick={() => handleSave(card)} style={{ fontSize: '0.9rem', padding: '0.4em 1em', background: '#3a6ea5', color: '#fff', border: 'none', borderRadius: 6 }} disabled={card.uploading}>Gem</button>
                    <button onClick={() => handleCancel(card)} style={{ fontSize: '0.9rem', padding: '0.4em 1em', background: '#bbb', color: '#222', border: 'none', borderRadius: 6 }}>Annuller</button>
                    <button onClick={() => handleDeleteCard(card.id)} style={{ color: '#e63946', fontSize: '0.9rem', padding: '0.4em 1em', background: '#fff', border: '1px solid #e63946', borderRadius: 6 }}>Slet</button>
                  </div>
                  {deleteConfirm === card.id && (
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.95)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 16 }}>
                      <div style={{ marginBottom: 16, fontWeight: 600 }}>Er du sikker på du vil slette dette kort?</div>
                      <button onClick={() => confirmDeleteCard(card.id)} style={{ background: '#e63946', color: '#fff', border: 'none', borderRadius: 8, padding: '0.5em 1.5em', marginRight: 8 }}>Ja, slet</button>
                      <button onClick={cancelDelete} style={{ background: '#bbb', color: '#222', border: 'none', borderRadius: 8, padding: '0.5em 1.5em', marginTop: 8 }}>Annuller</button>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: card.image_url ? `url(${card.image_url}) center/cover no-repeat` : bg,
                    borderRadius: 16,
                    position: 'relative',
                    cursor: isAdmin ? 'pointer' : 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'stretch',
                    minHeight: 180,
                  }}
                  onClick={isAdmin ? () => handleEditCard(card.id) : () => setEditAlert('Kun autoriserede brugere kan redigere kort. Log ind for at redigere.')}
                  title={isAdmin ? 'Rediger kortet' : 'Kun autoriserede brugere kan redigere kort'}
                >
                  {/* Count cirkel og op/ned knapper */}
                  <div className="remedier-card-counter" style={{ position: 'absolute', top: '50%', right: -10, transform: 'translateY(-50%)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                    <button style={{ border: 'none', background: '#fff', color: '#333', cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 16, lineHeight: 1, padding: 0, marginBottom: 2, borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', opacity: !isAdmin || (card.count ?? 1) >= 10 ? 0.5 : 1 }} onClick={() => {
                      if (!isAdmin) { setEditAlert('Kun autoriserede brugere kan redigere kort. Log ind for at redigere.'); return; }
                      if ((card.count ?? 1) >= 10) return;
                      handleCountChange(card.id, true);
                    }}>▲</button>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fff', border: '2px solid #bbb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, margin: '2px 0' }}>
                      x{card.count ?? 1}
                    </div>
                    <button style={{ border: 'none', background: '#fff', color: '#333', cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 16, lineHeight: 1, padding: 0, marginTop: 2, borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', opacity: !isAdmin || (card.count ?? 1) <= 1 ? 0.5 : 1 }} onClick={() => {
                      if (!isAdmin) { setEditAlert('Kun autoriserede brugere kan redigere kort. Log ind for at redigere.'); return; }
                      if ((card.count ?? 1) <= 1) return;
                      handleCountChange(card.id, false);
                    }}>▼</button>
                  </div>
                  {/* Kategori ikon flyttet ind i tekstfeltet */}
                  {icon && (
                    <img src={icon} alt={card.kategori} style={{ position: 'absolute', top: 8, right: 8, width: 36, height: 36, zIndex: 5 }} />
                  )}
                  <div style={{
                    background: textBg,
                    borderRadius: '0 0 16px 16px',
                    padding: '16px 12px 12px 12px',
                    minHeight: 60,
                    position: 'relative',
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginBottom: 6, color: textColor }}>{card.name}</div>
                    <div style={{ fontSize: '1.05rem', color: textColor }}>{card.text}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Skjult eksport-container med alle kort i "slutvisning" uden tæller */}
      {exporting && isAdmin && (
        <div style={{ position: 'fixed', left: -9999, top: 0, zIndex: -1 }}>
          {cards.filter(card => !card.editing && card.name && card.text).map((card, i) => {
            // Samme JSX som slutvisning, men uden tæller
            let bg = '#fff';
            let icon = null;
            let textBg = 'rgba(255,255,255,0.82)';
            let textColor = '#333';
            if (card.kategori === 'super-lorte-spell') {
              bg = '#ffe066';
              icon = '/lort2.png';
              textBg = 'rgba(255,224,102,0.55)';
            } else if (card.kategori === 'master-lorte-spell') {
              bg = '#22336c';
              icon = '/lort3.png';
              textBg = 'rgba(34,51,108,0.85)';
              textColor = '#fff';
            } else {
              bg = '#fff';
              icon = '/lort1.png';
              textBg = 'rgba(255,255,255,0.82)';
              textColor = '#333';
            }
            return (
              <div
                key={card.id || card.name + card.text}
                ref={el => exportRefs.current[i] = el}
                style={{
                  width: EXPORT_WIDTH,
                  height: EXPORT_HEIGHT,
                  background: card.image_url ? `url(${card.image_url}) center/cover no-repeat` : bg,
                  border: '2px solid #333',
                  borderRadius: 16,
                  boxShadow: '0 2px 12px #0002',
                  position: 'relative',
                  marginBottom: 20,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'flex-end',
                }}
              >
                {/* Tekstfelt */}
                <div style={{
                  background: textBg,
                  borderRadius: '0 0 16px 16px',
                  padding: '16px 12px 12px 12px',
                  minHeight: 60,
                  position: 'relative',
                }}>
                  {/* Kategori ikon */}
                  {icon && (
                    <img src={icon} alt={card.kategori} style={{ position: 'absolute', top: 8, right: 8, width: 36, height: 36, zIndex: 5 }} />
                  )}
                  <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginBottom: 6, color: textColor }}>{card.name}</div>
                  <div style={{ fontSize: '1.05rem', color: textColor }}>{card.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Alert besked hvis ikke autoriseret */}
      {editAlert && (
        <div style={{ position: 'fixed', top: 30, left: '50%', transform: 'translateX(-50%)', background: '#e63946', color: '#fff', padding: '1em 2em', borderRadius: 8, zIndex: 2000, fontWeight: 600, fontSize: 18, boxShadow: '0 2px 12px #0004' }}>
          {editAlert}
          <button onClick={() => setEditAlert('')} style={{ marginLeft: 16, background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', fontWeight: 700 }}>×</button>
        </div>
      )}
    </div>
  );
};

export default RemedierCards; 